import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useWebSocket } from './useWebSocket';
import * as notificationApi from '../services/notificationApi';

/**
 * Composable per la gestione delle notifiche dell'utente
 * Gestisce lo stato, il fetch e la sincronizzazione real-time via WebSocket
 */
export function useNotifications() {
  // State
  const notifications = ref([]);
  const unreadCount = ref(0);
  const loading = ref(false);
  const error = ref(null);
  const currentPage = ref(1);
  const pageSize = ref(10);
  const totalNotifications = ref(0);

  // WebSocket
  const { connect, on, off } = useWebSocket();

  /**
   * Fetch notifiche dell'utente (paginate)
   */
  const fetchNotifications = async (page = 1, unreadOnly = false) => {
    loading.value = true;
    error.value = null;

    try {
      const result = await notificationApi.getNotifications(page, pageSize.value, unreadOnly);

      if (result.success) {
        // Backend returns:
        // - success: true/false
        // - data: Array of notifications with readBy array (contains {userId, readAt})
        // - pagination: {page, limit, total, pages}
        Console.log('[useNotifications] Fetched notifications:', result.data);
        
        notifications.value = result.data.map(notif => ({
          ...notif,
          // isRead is already set by backend based on current user
          // The backend returns notifications with readBy that may contain current user
          isRead: notif.isRead !== undefined ? notif.isRead : false,
        }));
        totalNotifications.value = result.pagination.total;
        currentPage.value = page;
      } else {
        error.value = result.error?.message || 'Errore nel caricamento notifiche';
      }
    } catch (err) {
      Console.error('[useNotifications] Error fetching notifications:', err);
      error.value = err.message || 'Errore nel caricamento notifiche';
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch conteggio notifiche non lette
   */
  const fetchUnreadCount = async () => {
    try {
      unreadCount.value = await notificationApi.getUnreadCount();
    } catch (err) {
      Console.error('[useNotifications] Error fetching unread count:', err);
    }
  };

  /**
   * Segna una singola notifica come letta
   */
  const markAsRead = async (notificationId) => {
    try {
      Console.log('[useNotifications] Marking notification as read:', notificationId);
      const result = await notificationApi.markAsRead(notificationId);

      if (result.success) {
        // Aggiorna lo stato locale
        const notif = notifications.value.find(n => n._id === notificationId);
        if (notif) {
          Console.log('[useNotifications] Updated notification', notificationId, 'isRead: true');
          notif.isRead = true;
        }

        // Decrementa il contatore
        if (unreadCount.value > 0) {
          unreadCount.value--;
        }
      }
    } catch (err) {
      Console.error('[useNotifications] Error marking notification as read:', err);
    }
  };

  /**
   * Segna tutte le notifiche come lette
   */
  const markAllAsRead = async () => {
    try {
      const result = await notificationApi.markAllAsRead();

      if (result.success) {
        // Aggiorna lo stato locale
        notifications.value.forEach(notif => {
          notif.isRead = true;
        });

        // Azzera il contatore
        unreadCount.value = 0;
      }
    } catch (err) {
      Console.error('[useNotifications] Error marking all as read:', err);
    }
  };

  /**
   * Gestisce l'evento WebSocket di nuova notifica
   */
  const handleNewNotification = (notificationData) => {
    Console.log('[useNotifications] Received new_notification event:', notificationData);

    // Aggiorna il conteggio
    unreadCount.value++;

    // Se la lista è già caricata, aggiungi la nuova notifica in cima
    if (notifications.value.length > 0) {
      notifications.value.unshift({
        _id: notificationData.notificationId,
        title: notificationData.title,
        message: notificationData.message,
        actionUrl: notificationData.actionUrl,
        actionLabel: notificationData.actionLabel,
        priority: notificationData.priority || 'normal',
        notificationType: notificationData.notificationType || 'info',
        createdAt: notificationData.createdAt,
        isRead: false,
      });
    }
  };

  /**
   * Gestisce l'evento WebSocket di aggiornamento conteggio
   */
  const handleUnreadCountUpdate = (data) => {
    Console.log('[useNotifications] Received unread_count_update event:', data);
    unreadCount.value = data.unreadCount;
  };

  /**
   * Setup WebSocket listeners
   */
  const setupWebSocketListeners = () => {
    const socket = connect();

    socket.on('new_notification', handleNewNotification);
    socket.on('unread_count_update', handleUnreadCountUpdate);
  };

  /**
   * Rimuovi WebSocket listeners
   */
  const removeWebSocketListeners = () => {
    const socket = connect();

    socket.off('new_notification', handleNewNotification);
    socket.off('unread_count_update', handleUnreadCountUpdate);
  };

  /**
   * Lifecycle hooks
   */
  onMounted(() => {
    // Fetch iniziale
    fetchUnreadCount();

    // Setup WebSocket
    setupWebSocketListeners();
  });

  onUnmounted(() => {
    // Cleanup
    removeWebSocketListeners();
  });

  // Helper per ottenere l'ID utente corrente (dalle store o dal localStorage)
  const getCurrentUserId = () => {
    // TODO: Usare la store di autenticazione per ottenere l'ID utente
    // Per ora ritorna undefined, il server gestirà in base a req.user
    return undefined;
  };

  /**
   * Computed properties
   */
  const hasUnread = computed(() => unreadCount.value > 0);

  const totalPages = computed(() => {
    return Math.ceil(totalNotifications.value / pageSize.value);
  });

  return {
    // State
    notifications,
    unreadCount,
    loading,
    error,
    currentPage,
    pageSize,
    totalNotifications,

    // Computed
    hasUnread,
    totalPages,

    // Methods
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
  };
}

export default useNotifications;
